import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, getDocs, query, limit, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RecipeCard } from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ChevronRight, Utensils, Clock, Heart } from 'lucide-react';

const categories = [
  { id: 'fruehstueck', name: 'Frühstück', image: 'https://images.pexels.com/photos/376464/pexels-photo-376464.jpeg' },
  { id: 'mittagessen', name: 'Mittagessen', image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg' },
  { id: 'abendessen', name: 'Abendessen', image: 'https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg' },
  { id: 'dessert', name: 'Desserts', image: 'https://images.pexels.com/photos/5429051/pexels-photo-5429051.png' },
];

const features = [
  { icon: Utensils, title: 'Vielfältige Rezepte', description: 'Entdecke Gerichte aus aller Welt' },
  { icon: Clock, title: 'Schnell & Einfach', description: 'Rezepte für jeden Zeitplan' },
  { icon: Heart, title: 'Favoriten speichern', description: 'Merke dir deine Lieblingsrezepte' },
];

export const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredRecipes, setFeaturedRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedRecipes = async () => {
      try {
        const q = query(collection(db, 'recipes'), orderBy('createdAt', 'desc'), limit(6));
        const snapshot = await getDocs(q);
        const recipes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFeaturedRecipes(recipes);
      } catch (error) {
        console.error('Error fetching recipes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedRecipes();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/rezepte?suche=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"
            alt="Delicious food spread"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/50 to-bone-DEFAULT" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            Willkommen bei<br />
            <span className="text-terracotta">Schmackofatz</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto"
          >
            Entdecke köstliche Rezepte aus aller Welt und finde Inspiration für deine nächste Mahlzeit.
          </motion.p>

          {/* Search Bar */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleSearch}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
              <Input
                type="text"
                placeholder="Rezept oder Zutat suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 rounded-xl text-base bg-white shadow-lg border-none"
                data-testid="hero-search-input"
              />
            </div>
            <Button 
              type="submit"
              className="bg-terracotta hover:bg-terracotta/90 text-white rounded-xl px-8 py-6 font-semibold shadow-lg"
              data-testid="hero-search-btn"
            >
              Suchen
            </Button>
          </motion.form>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-bone-alt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-8 text-center shadow-sm"
              >
                <div className="w-16 h-16 bg-terracotta/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-terracotta" />
                </div>
                <h3 className="font-display text-xl font-semibold text-navy mb-2">{feature.title}</h3>
                <p className="text-olive">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
                Kategorien
              </h2>
              <p className="text-olive">Finde Rezepte nach Kategorie</p>
            </div>
            <Link to="/rezepte">
              <Button variant="ghost" className="text-terracotta hover:text-terracotta/80 gap-1">
                Alle ansehen <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <Link 
                  to={`/rezepte?kategorie=${category.id}`}
                  className="block relative aspect-[4/5] rounded-3xl overflow-hidden group"
                  data-testid={`category-${category.id}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                    <h3 className="font-display text-lg sm:text-xl font-semibold text-white">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Recipes Section */}
      <section className="py-20 bg-bone-alt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-2">
                Neueste Rezepte
              </h2>
              <p className="text-olive">Frisch aus unserer Küche</p>
            </div>
            <Link to="/rezepte">
              <Button variant="ghost" className="text-terracotta hover:text-terracotta/80 gap-1">
                Alle ansehen <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl h-80 animate-pulse" />
              ))}
            </div>
          ) : featuredRecipes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredRecipes.map((recipe, index) => (
                <RecipeCard key={recipe.id} recipe={recipe} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-olive text-lg mb-4">Noch keine Rezepte vorhanden.</p>
              <Link to="/rezepte">
                <Button className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-8">
                  Rezepte entdecken
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

          {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-terracotta rounded-3xl p-12 text-white"
          >
            <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
              Bereit zum Kochen?
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Registriere dich jetzt und speichere deine Lieblingsrezepte für später.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/registrieren">
                <Button 
                  className="bg-white text-terracotta hover:bg-white/90 rounded-full px-8 py-6 font-semibold text-lg"
                  data-testid="cta-register-btn"
                >
                  Jetzt registrieren
                </Button>
              </Link>
              <Link to="/setup-admin">
                <Button 
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold text-lg"
                  data-testid="cta-admin-btn"
                >
                  Admin werden
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
